import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Eye, EyeOff, Lock, CheckCircle, AlertCircle, Loader, Shield, ArrowLeft } from 'lucide-react';
import Logo from '../../components/ui/Logo';
import { supabase } from '../../lib/supabase';
import { useToast } from '../../components/ui/Toast';

const getPasswordStrength = (pwd) => {
  if (!pwd) return { score: 0, label: '', color: '' };
  let score = 0;
  if (pwd?.length >= 8) score++;
  if (pwd?.length >= 12) score++;
  if (/[A-Z]/?.test(pwd)) score++;
  if (/[0-9]/?.test(pwd)) score++;
  if (/[^A-Za-z0-9]/?.test(pwd)) score++;
  const levels = [
    { score: 0, label: '', color: '' },
    { score: 1, label: 'Very Weak', color: 'bg-red-500' },
    { score: 2, label: 'Weak', color: 'bg-orange-500' },
    { score: 3, label: 'Fair', color: 'bg-yellow-500' },
    { score: 4, label: 'Strong', color: 'bg-blue-500' },
    { score: 5, label: 'Very Strong', color: 'bg-green-500' },
  ];
  return levels?.[score] || levels?.[0];
};

const ResetPassword = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const toast = useToast();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [sessionLoading, setSessionLoading] = useState(true);
  const [hasSession, setHasSession] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({ password: '', confirm: '' });

  useEffect(() => {
    const initSession = async () => {
      if (!supabase) {
        setSessionLoading(false);
        setError('Authentication service unavailable.');
        return;
      }

      try {
        // Check for hash-based tokens (implicit flow from Supabase email)
        const hash = window?.location?.hash?.replace('#', '');
        const hashParams = hash ? Object.fromEntries(new URLSearchParams(hash)) : {};

        // Handle implicit flow: access_token + refresh_token in hash
        if (hashParams?.access_token && hashParams?.refresh_token) {
          const { data, error: sessionError } = await supabase?.auth?.setSession({
            access_token: hashParams?.access_token,
            refresh_token: hashParams?.refresh_token,
          });
          if (sessionError) {
            setError('This password reset link has expired or is invalid. Please request a new one.');
          } else if (data?.session) {
            setHasSession(true);
            try { window.history?.replaceState(null, '', window.location?.pathname); } catch {}
          } else {
            setError('Invalid reset session. Please request a new password reset link.');
          }
          setSessionLoading(false);
          return;
        }

        // Handle PKCE flow: token_hash in search params
        const tokenHash = searchParams?.get('token_hash');
        if (tokenHash) {
          const { data, error: verifyError } = await supabase?.auth?.verifyOtp({
            token_hash: tokenHash,
            type: 'recovery',
          });
          if (verifyError) {
            setError('This password reset link has expired or is invalid. Please request a new one.');
          } else if (data?.session || data?.user) {
            setHasSession(true);
            try { window.history?.replaceState(null, '', window.location?.pathname); } catch {}
          } else {
            setError('Invalid reset session. Please request a new password reset link.');
          }
          setSessionLoading(false);
          return;
        }

        // Check for existing session (user navigated here after PASSWORD_RECOVERY event)
        const { data: { session } } = await supabase?.auth?.getSession();
        if (session) {
          setHasSession(true);
        } else {
          setError('This password reset link has expired or is invalid. Please request a new one.');
        }
      } catch (err) {
        setError('Failed to validate reset session. Please try again.');
      } finally {
        setSessionLoading(false);
      }
    };

    initSession();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const validatePassword = () => {
    const errors = { password: '', confirm: '' };
    if (!password) errors.password = 'Password is required';
    else if (password?.length < 8) errors.password = 'Password must be at least 8 characters';
    else if (!/(?=.*[a-z])(?=.*[A-Z])/?.test(password)) errors.password = 'Must contain uppercase and lowercase letters';
    if (!confirmPassword) errors.confirm = 'Please confirm your password';
    else if (password !== confirmPassword) errors.confirm = 'Passwords do not match';
    return errors;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    const errors = validatePassword();
    setFieldErrors(errors);
    if (errors?.password || errors?.confirm) return;

    setLoading(true);
    setError('');
    try {
      const { error: updateError } = await supabase?.auth?.updateUser({ password });
      if (updateError) {
        setError(updateError?.message || 'Failed to update password.');
        toast?.error(updateError?.message || 'Failed to update password.', 'Error');
      } else {
        setSuccess(true);
        toast?.success('Password updated successfully! Please sign in with your new password.', 'Password Updated ✓', 6000);
        // Sign out to force fresh login with new password
        try { await supabase?.auth?.signOut(); } catch {}
        setTimeout(() => navigate('/login', { replace: true }), 2500);
      }
    } catch (err) {
      setError(err?.message || 'An unexpected error occurred.');
      toast?.error(err?.message || 'An unexpected error occurred.', 'Error');
    } finally {
      setLoading(false);
    }
  };

  const pwdStrength = getPasswordStrength(password);

  if (sessionLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50 flex items-center justify-center">
        <div className="text-center">
          <Loader className="w-8 h-8 animate-spin text-blue-600 mx-auto mb-3" />
          <p className="text-gray-600 text-sm">Validating reset link...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Logo size="large" variant="default" showText={true} linkTo="/" />
        </div>

        <div className="bg-white rounded-2xl shadow-xl p-8">
          {success ? (
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-3">Password Updated!</h2>
              <p className="text-gray-600 mb-6">Your password has been successfully updated. Redirecting to sign in...</p>
              <Link to="/login" className="inline-flex items-center space-x-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors">
                <span>Sign In Now</span>
              </Link>
            </div>
          ) : !hasSession ? (
            <div className="text-center">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <AlertCircle className="w-8 h-8 text-red-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-3">Link Expired</h2>
              <p className="text-gray-600 mb-8">{error}</p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Link to="/login" className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold transition-colors text-center">
                  Request New Link
                </Link>
                <Link to="/" className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-50 transition-colors text-center">
                  Go Home
                </Link>
              </div>
            </div>
          ) : (
            <>
              <div className="text-center mb-8">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Lock className="w-8 h-8 text-blue-600" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Set New Password</h2>
                <p className="text-gray-600 text-sm">Choose a strong password for your account.</p>
              </div>

              {error && (
                <div className="mb-5 p-4 bg-red-50 border border-red-200 rounded-xl flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">New Password</label>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={e => { setPassword(e?.target?.value); setFieldErrors(p => ({ ...p, password: '' })); }}
                      className={`w-full px-4 py-3 border rounded-xl text-sm focus:outline-none focus:ring-2 ${
                        fieldErrors?.password ? 'border-red-300 focus:ring-red-200' : 'border-gray-300 focus:ring-blue-200'
                      }`}
                      placeholder="Enter new password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(p => !p)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  {fieldErrors?.password && <p className="text-xs text-red-600 mt-1">{fieldErrors?.password}</p>}
                  {password && (
                    <div className="mt-2">
                      <div className="flex gap-1 mb-1">
                        {[1,2,3,4,5]?.map(i => (
                          <div key={i} className={`h-1.5 flex-1 rounded-full transition-colors ${
                            i <= pwdStrength?.score ? pwdStrength?.color : 'bg-gray-200'
                          }`} />
                        ))}
                      </div>
                      {pwdStrength?.label && (
                        <p className="text-xs text-gray-500">{pwdStrength?.label}</p>
                      )}
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Confirm Password</label>
                  <div className="relative">
                    <input
                      type={showConfirm ? 'text' : 'password'}
                      value={confirmPassword}
                      onChange={e => { setConfirmPassword(e?.target?.value); setFieldErrors(p => ({ ...p, confirm: '' })); }}
                      className={`w-full px-4 py-3 border rounded-xl text-sm focus:outline-none focus:ring-2 ${
                        fieldErrors?.confirm ? 'border-red-300 focus:ring-red-200' : 'border-gray-300 focus:ring-blue-200'
                      }`}
                      placeholder="Confirm new password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirm(p => !p)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showConfirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  {fieldErrors?.confirm && <p className="text-xs text-red-600 mt-1">{fieldErrors?.confirm}</p>}
                  {confirmPassword && password === confirmPassword && (
                    <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
                      <CheckCircle className="w-3 h-3" /> Passwords match
                    </p>
                  )}
                </div>

                <div className="flex items-center gap-2 text-xs text-gray-500 bg-gray-50 rounded-lg px-3 py-2">
                  <Shield className="w-4 h-4 text-green-500" />
                  <span>Use 8+ characters with uppercase, lowercase, and numbers.</span>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full flex items-center justify-center gap-2 py-3 bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white rounded-xl font-semibold transition-colors"
                >
                  {loading ? <Loader className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />}
                  <span>{loading ? 'Updating...' : 'Update Password'}</span>
                </button>
              </form>

              <div className="mt-6 text-center">
                <Link to="/login" className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 transition-colors">
                  <ArrowLeft className="w-4 h-4" />
                  Back to Sign In
                </Link>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;